"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Search, ShieldCheck, Droplets, Wind, ClipboardList, Hammer, CircleCheck as CheckCircle2, ArrowRight, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface StepItem {
  id: number;
  number: string;
  title: string;
  description: string;
  primaryLinkTo: string;
  primaryLinkLabel: string;
  iconName: string;
}
interface StepTag {
  id: number;
  stepId: number;
  label: string;
  to: string;
}
const stepsData: StepItem[] = [{
  id: 1,
  number: "01",
  title: "Emergency contact",
  description: "Call us 24/7. You speak directly with our local dispatched crew, never a detached third-party call center.",
  primaryLinkTo: "/ContactRequestServicePage",
  primaryLinkLabel: "Contact local crew",
  iconName: "PhoneCall"
}, {
  id: 2,
  number: "02",
  title: "Assessment",
  description: "Our technicians arrive on-site with thermal imaging and moisture meters to map the full boundary of the damage.",
  primaryLinkTo: "",
  primaryLinkLabel: "",
  iconName: "Search"
}, {
  id: 3,
  number: "03",
  title: "Mitigation",
  description: "We secure the property, shut down secondary hazard risks, and stop further spread through immediate containment barriers.",
  primaryLinkTo: "/WaterDamageFloodRestorationPage",
  primaryLinkLabel: "Water & flood mitigation",
  iconName: "ShieldCheck"
}, {
  id: 4,
  number: "04",
  title: "Water extraction / cleanup",
  description: "Industrial extractors remove standing water while specialized safety protocols address hazardous waste, smoke residue, or mold.",
  primaryLinkTo: "",
  primaryLinkLabel: "",
  iconName: "Droplets"
}, {
  id: 5,
  number: "05",
  title: "Structural drying / remediation",
  description: "Commercial dehumidifiers and air movers run until psychrometric moisture readings confirm framing and drywall match baseline standards.",
  primaryLinkTo: "",
  primaryLinkLabel: "",
  iconName: "Wind"
}, {
  id: 6,
  number: "06",
  title: "Documentation",
  description: "We prepare thorough digital logs, thermal photos, and Xactimate line-item reports to streamline your insurance claim.",
  primaryLinkTo: "",
  primaryLinkLabel: "",
  iconName: "ClipboardList"
}, {
  id: 7,
  number: "07",
  title: "Restoration / reconstruction",
  description: "Our licensed builders reconstruct damaged framing, drywall, electrical, and finishes to return your space to pre-loss condition.",
  primaryLinkTo: "/ReconstructionDesignBuildPage",
  primaryLinkLabel: "Reconstruction services",
  iconName: "Hammer"
}, {
  id: 8,
  number: "08",
  title: "Final walkthrough",
  description: "We walk room by room with you and provide final moisture clearance certificates to confirm complete restoration.",
  primaryLinkTo: "/ContactRequestServicePage",
  primaryLinkLabel: "Request a walkthrough",
  iconName: "CheckCircle2"
}];
const serviceSubLinks: StepTag[] = [{
  id: 1,
  stepId: 4,
  label: "Mold remediation",
  to: "/MoldRemediationPage"
}, {
  id: 2,
  stepId: 4,
  label: "Fire & smoke",
  to: "/FireSmokeOdorRestorationPage"
}, {
  id: 3,
  stepId: 4,
  label: "Sewage cleanup",
  to: "/SewageCleanupPage"
}, {
  id: 4,
  stepId: 4,
  label: "Biohazard",
  to: "/BiohazardCleanupPage"
}, {
  id: 5,
  stepId: 4,
  label: "Asbestos & lead",
  to: "/AsbestosLeadAbatementPage"
}];
function StepIcon({
  name
}: {
  name: string;
}) {
  const iconClass = "size-5 text-primary";
  switch (name) {
    case "PhoneCall":
      return <PhoneCall className={iconClass} />;
    case "Search":
      return <Search className={iconClass} />;
    case "ShieldCheck":
      return <ShieldCheck className={iconClass} />;
    case "Droplets":
      return <Droplets className={iconClass} />;
    case "Wind":
      return <Wind className={iconClass} />;
    case "ClipboardList":
      return <ClipboardList className={iconClass} />;
    case "Hammer":
      return <Hammer className={iconClass} />;
    case "CheckCircle2":
      return <CheckCircle2 className={iconClass} />;
    default:
      return <ShieldCheck className={iconClass} />;
  }
}
export default function RestorationProcess() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencyrestorationprocess" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-3.5 border-primary/30 text-primary bg-primary/5 px-3 py-1 font-medium tracking-wide uppercase text-[11px]">
            Clear, Direct Procedure
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Our 8-step restoration process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From your initial call to the final sign-off, our local crews handle
            every phase directly and coordinate closely with you and your
            insurer.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stepsData.map((step, index) => {
          const stepTags = serviceSubLinks.filter(tag => tag.stepId === step.id);
          return <motion.div key={step.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.15
          }} transition={{
            duration: 0.45,
            delay: index * 0.05
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="font-mono text-xs font-semibold text-primary px-2.5 py-1 rounded-md bg-primary/10">
                        STEP {step.number}
                      </span>
                      <div className="p-2 rounded-lg bg-muted text-foreground flex items-center justify-center">
                        <StepIcon name={step.iconName} />
                      </div>
                    </div>
                    <CardTitle className="text-lg font-default font-semibold text-foreground tracking-tight">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="pt-1 flex flex-col justify-between flex-1 gap-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>

                    <div className="pt-2 mt-auto">
                      {step.primaryLinkTo ? <div className="pt-2 border-t border-border">
                          <Link to={step.primaryLinkTo} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors group">
                            <span>{step.primaryLinkLabel}</span>
                            <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                          </Link>
                        </div> : null}

                      {stepTags.length > 0 ? <div className="pt-2 border-t border-border flex flex-col gap-1.5">
                          <span className="text-[11px] font-medium text-muted-foreground">
                            Related cleanup services:
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {stepTags.map((subLink, subIndex) => <Link key={subLink.id} data-index={subIndex} to={subLink.to} className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-sm bg-muted text-foreground hover:bg-primary/10 hover:text-primary transition-colors">
                                {subLink.label}
                              </Link>)}
                          </div>
                        </div> : null}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-12 sm:mt-16 p-6 sm:p-8 rounded-2xl bg-muted/60 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="text-lg font-semibold font-default text-foreground">
              Facing active property damage right now?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Direct regional crews are ready to dispatch 24/7 across Maryland,
              DC, and Northern Virginia.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
              <span>Request immediate service</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}